/**
 * Die Umformung einer Bestellung von der Drahtform des Servers in die Form der Oberflaeche:
 * `entschachtele` (Extras flach, Lieferant als Name, Aufgaben und Raten durch ihre Nahtfunktionen),
 * dazu `alsAufgabe` (`title` -> `name`) und `alsRate` (`dueDate`/`paidAt` -> `due`/`paidDate`).
 *
 * Bis zum KI-Zugang (Stufe 1) standen die drei Funktionen in `api/bestellungen.ts`, `api/aufgaben.ts`
 * und `api/zahlungsraten.ts`. Sie sind UNVERAENDERT hierher verschoben, weil der Server
 * (`backend/src/ki/auswertung/frontend/`) dieselbe Umformung fahren muss wie die Oberflaeche und
 * deshalb eine zeichengleiche Kopie dieses Moduls braucht; die drei Dateien fuehren die Namen als
 * Re-Export weiter, kein Aufrufer musste umziehen. Die Begruendungen zur Drahtform (Verschachtelung
 * der Antwort, Feldgruppen, `title` gegen `name`) stehen weiter in den Kopfkommentaren von
 * `api/bestellungen.ts` und `api/aufgaben.ts`.
 *
 * Regel fuer dieses Modul (`test/lib/reineKette.test.ts`): keine Importe ausser `.ts` in
 * `frontend/src`, kein `react`, kein `import.meta`, keine Browser-Globals. Es braucht derzeit keinen
 * einzigen Import.
 */

/**
 * Serverform einer Aufgabe -> Form der Oberflaeche. Heute genau eine Umbenennung (`title` -> `name`,
 * siehe Kopfkommentar von `api/aufgaben.ts`); alles uebrige heisst auf beiden Seiten gleich und laeuft
 * unveraendert durch.
 *
 * `title` faellt dabei WEG statt daneben stehen zu bleiben: zwei Namen fuer denselben Wert im selben
 * Objekt laufen frueher oder spaeter auseinander, und eine Lesestelle, die versehentlich `title`
 * nimmt, faende ihn hier und im naechsten Datensatz vielleicht nicht.
 *
 * Ueber Destrukturierung und Spread, nicht ueber indizierten Schreibzugriff - `"__proto__"` aus
 * Fremddaten setzt sonst den Prototypen (dieselbe Begruendung wie in `entschachtele`).
 */
export function alsAufgabe(roh: unknown): unknown {
  if (typeof roh !== "object" || roh === null || Array.isArray(roh)) return roh;
  const { title, ...rest } = roh as Record<string, unknown> & { title?: unknown };
  return title === undefined ? roh : { ...rest, name: title };
}

/**
 * Naht fuer vom Server geladene Raten (Auftrag 2026-10-01, Fehler 3). Der Server liefert `dueDate`
 * und `paidAt`; die Oberflaeche liest an allen Stellen `due`/`paid`/`paidDate` (Altform aus
 * `index.html`). Ohne diese Uebersetzung sah `paymentCalendarRows` das gespeicherte Datum nie und
 * fiel auf die Faelligkeit der Bestellung zurueck. Sie sitzt HIER an der einen Naht (gerufen von
 * `entschachtele` unten, Vorbild `alsAufgabe`), nicht an jeder Lesestelle.
 * `dueDate` faellt weg, damit kein zweiter Wert neben `due` stehen bleibt (siehe AGENTS.md,
 * Abschnitt due/dueDate). Ein vorhandenes `due` gewinnt (lokale Zeile).
 */
export function alsRate(roh: unknown): unknown {
  if (typeof roh !== "object" || roh === null || Array.isArray(roh)) return roh;
  const { dueDate, paidAt, ...rest } = roh as Record<string, unknown>;
  const zeile: Record<string, unknown> = { ...rest };
  if (zeile.due === undefined || zeile.due === null || zeile.due === "") {
    zeile.due = dueDate === undefined || dueDate === null ? "" : String(dueDate).slice(0, 10);
  }
  if (zeile.paid === undefined) zeile.paid = zeile.status === "paid";
  if (zeile.paidDate === undefined && paidAt !== undefined && paidAt !== null) {
    zeile.paidDate = String(paidAt).slice(0, 10);
  }
  return zeile;
}

/**
 * Leitet `supplierId` aus `kLieferant` her - GEMESSEN, nicht angenommen, so wie der Rest dieser
 * Naht.
 *
 * DIE KETTE, DIE OHNE DAS HIER JEDE BESTELLUNG ZUR DOLLAR-BESTELLUNG MACHTE: `GET /api/orders` und
 * `GET /api/orders/:id` liefern am Kopf `kLieferant` (Spalte `orders.k_lieferant`, camelCased von
 * `oeffentlich()`, `backend/src/domaene/oeffentlich.ts:7-9` - beide Lesewege teilen dieselbe Abfrage
 * `BESTELLUNG_MIT_EXTRAS`, `backend/src/domaene/orders.ts:128-131`). `supplierId` liefert dagegen
 * KEINE Liveroute (`grep -rn "supplierId" backend/src` trifft ausschliesslich Migrationscode,
 * `backend/src/migration/ruecklese.ts:243`). Ohne Herleitung blieb `lib/suppliers.ts#orderSupplier`
 * damit auf dem Namenszweig angewiesen, den die Route ebenfalls nicht liefert - `orderCurrency` fiel
 * mangels aufgeloestem Lieferanten immer auf `"USD"` zurueck, auch fuer einen EUR-Lieferanten. Das
 * Backend hat genau das selbst dokumentiert (`ruecklese.ts:38-41`).
 *
 * DIE FORM IST `String(kLieferant)`, keine andere: `api/lieferanten.ts#entschachtele` setzt
 * `id: String(kLieferant)` (dort Zeile 157), und `lib/suppliers.ts#supplierById` vergleicht STRENG
 * (`s.id === id`, dort Zeile 169) - eine Zahl gegen einen String verglichen trifft mit `===` nie.
 *
 * NULL/UNDEFINED WERDEN NICHT ZU STRING: `k_lieferant` ist NULL-faehig, weil ein Entwurf entstehen
 * darf, bevor der Lieferant gewaehlt ist (`backend/migrations/005_domaene.sql:21-23`). `String(null)`
 * waere die wahrheitswertige Zeichenkette `"null"` und loeste in `state/Speicher.tsx#bestandLaden`
 * einen sinnlosen Einzelabruf aus - der dortige `filter(Boolean)` faengt nur einen LEEREN String,
 * nicht `"null"`. Ein Entwurf ohne Lieferanten bleibt deshalb ohne `supplierId`.
 *
 * KEINE UEBERSCHREIBUNG - aber GENAU BESEHEN: traegt der Kopf schon eine eigene, nichtleere
 * `supplierId` ALS ZEICHENKETTE, bleibt sie stehen. Ein Kopfwert anderen Typs (etwa eine Zahl)
 * bliebe NICHT stehen - er wuerde durch die Herleitung aus `kLieferant` ersetzt.
 *
 * Das ist bewusst so und kein Versehen: `supplierId` ist an dieser Naht als Zeichenkette definiert,
 * weil `supplierById` streng vergleicht (siehe oben). Eine Zahl an dieser Stelle waere bereits ein
 * Formfehler, und sie stehen zu lassen hiesse, ihn weiterzureichen.
 *
 * GEMESSEN kommt beides auf keinem Liveweg vor (derselbe `grep` wie oben) - die Herleitung greift
 * heute immer zusaetzlich, nie ersetzend; der Riegel steht fuer den Fall, dass sich das aendert.
 *
 * Die Einschraenkung steht hier ausdruecklich, weil die urspruengliche Fassung dieses Absatzes
 * "bleibt sie stehen" OHNE die Typbedingung behauptete - eine Aussage, die allgemeiner war als der
 * Code darunter. Genau diese Klasse (eine Zusicherung im Kommentar, die weiter reicht als das
 * Gepruefte) ist in diesem Projekt mehrfach als Reviewbefund zurueckgekommen.
 */
function supplierIdVonKopf(kopf: Record<string, unknown>): string | undefined {
  if (typeof kopf.supplierId === "string" && kopf.supplierId !== "") {
    return kopf.supplierId;
  }
  const kLieferant = kopf.kLieferant;
  return kLieferant === null || kLieferant === undefined ? undefined : String(kLieferant);
}

/**
 * Schwester von `supplierIdVonKopf` oben: macht aus dem `supplier`-OBJEKT des Einzelabrufs den
 * NAMENSSTRING, den die Oberflaeche an dieser Stelle erwartet.
 *
 * DER FEHLERFALL, GEGEN DEN DAS GEBAUT IST - ein ABSTURZ, kein Anzeigefehler:
 * `GET /api/orders/:id` setzt `supplier` auf das Ergebnis von `ladeLieferant(...)`
 * (`backend/src/domaene/orders.ts#ladeBestellung`), also
 * `{ kLieferant, jtl: JtlLieferant | null, extras }`. Die SPA liest `o.supplier` an rund
 * FUENFUNDZWANZIG Stellen in DREIZEHN Dateien als Zeichenkette. Drei davon werfen zur Laufzeit:
 *
 *   `Drawer/MainTab/Stammdaten.tsx#supplierNames`  ->  `.trim()` auf einem Objekt: TypeError
 *   `lib/suppliers.ts` (Namensmenge)               ->  dasselbe
 *   `Drawer/MainTab/Stammdaten.tsx` (Anzeige)      ->  Objekt als React-Kind: wirft
 *
 * Alle uebrigen (Filter, Sortierung, Suche, Boardzeile, Betreffzeile einer E-Mail) zeigen
 * `[object Object]`.
 *
 * ZU DEN ZAHLEN HIER: sie sind zweimal berichtigt worden - erst stand "zehn Stellen", dann
 * "neunzehn in neun Dateien", beide Male zu niedrig gegriffen. Wer sie wieder braucht, ZAEHLT SIE
 * NACH (`grep -rn "\.supplier\b" frontend/src`), statt diese Angabe fortzuschreiben. Eine
 * berichtigte Zahl, die wieder danebenliegt, ist schlimmer als die urspruengliche - genau deshalb
 * steht hier "rund" und nicht eine genaue Zahl.
 *
 * AUSLOESER IST NICHTS EXOTISCHES: Drawer oeffnen, IRGENDEIN Feld speichern - danach laedt
 * `App.tsx#drawerAendern` die Bestellung einzeln nach, und der Datensatz liegt mit dem Objekt im
 * Store. `supplierNames` laeuft dabei ueber ALLE Bestellungen im Store; ein einziger solcher
 * Datensatz reisst die ganze Lieferantenauswahl mit.
 *
 * DER NAME KOMMT AUS `jtl.firma` - dieselbe Quelle, aus der `api/lieferanten.ts#entschachtele`
 * bereits `name` herleitet. Die beiden duerfen nicht auseinanderlaufen.
 *
 * `jtl` IST NULL-FAEHIG, UND DAS IST DER NORMALFALL BEIM ERSTEN START: ohne konfigurierten
 * JTL-Lesezugang gibt es keinen Lieferantenstamm (`deploy/ENV-CHECKLISTE.md`, Abschnitt 4.1 - der
 * Block ist optional, und ohne ihn laeuft der Dienst). Dann gibt es KEINEN Namen, und diese Funktion
 * liefert `undefined`; `entschachtele` laesst den Schluessel dann weg.
 *
 * WARUM `undefined` UND NICHT `""`: "kein Lieferant" ist ein Zustand, den der Bestand kennt, und
 * beide Lesestellen, die ueberhaupt unterscheiden koennten, behandeln fehlend und leer gleich
 * (`lib/orderList.ts`: `o.supplier ? … : empty` und `String(o.supplier || "")`). Ein leerer String
 * waere also nur eine zweite Schreibweise fuer denselben Zustand - und `undefined` haelt es mit
 * `supplierIdVonKopf` oben, das im selben Fall ebenfalls nichts setzt.
 *
 * EINE VORHANDENE ZEICHENKETTE BLEIBT UNANGETASTET - auch eine leere. Der Riegel greift ausdruecklich
 * fuer `typeof === "string"`; alles andere (Objekt, Zahl, `null`) laeuft in die Herleitung. Das ist
 * bewusst so: an dieser Naht ist `supplier` als Zeichenkette definiert, und ein anderer Typ ist
 * bereits ein Formfehler, den weiterzureichen niemandem hilft.
 */
function supplierNameVonKopf(kopf: Record<string, unknown>): string | undefined {
  const supplier = kopf.supplier;
  if (typeof supplier === "string") return supplier;
  if (typeof supplier !== "object" || supplier === null || Array.isArray(supplier)) return undefined;
  const jtl = (supplier as { jtl?: unknown }).jtl;
  if (typeof jtl !== "object" || jtl === null || Array.isArray(jtl)) return undefined;
  const firma = (jtl as { firma?: unknown }).firma;
  // Der LEERE Name zaehlt als "kein Name" - sonst haetten die beiden Lesewege verschiedene
  // SCHLUESSELMENGEN (Einzelabruf mit `supplier: ""`, Sammelroute ohne den Schluessel), und genau das
  // schliesst der Kopfkommentar von `entschachtele` unten aus. Der Fall ist erreichbar:
  // `leseLieferant` (`backend/src/jtl/lieferanten.ts`) setzt `firma: alsText(zeile.cFirma) ?? ""`,
  // und `alsText` (`backend/src/jtl/werte.ts`) liefert `null` fuer NULL, "" UND reinen Leerraum -
  // ein JTL-Lieferant mit leerem `cFirma` erzeugt also wirklich `jtl.firma === ""`.
  // Die Schwesterfunktion `supplierIdVonKopf` oben riegelt den leeren String aus demselben Grund ab.
  // Keine Lesestelle braeche daran (alle behandeln "" und `undefined` gleich) - es geht um die Form,
  // die die beiden Wege ueberhaupt erst vergleichbar macht.
  return typeof firma === "string" && firma !== "" ? firma : undefined;
}

/**
 * Loest die Verschachtelung der Antwort auf: `{ …kopf, extras: { … } }` -> ein flacher Datensatz.
 *
 * DREI Meta-Spalten der `order_extras`-ZEILE wandern dabei NICHT flach mit, weil jede von ihnen am
 * Kopf der Bestellung denselben Namen mit einer ANDEREN Bedeutung traegt:
 *
 *   `orderId`               == `id` der Bestellung, also reine Wiederholung -> faellt weg
 *   `rowVersion`            == Version der order_extras-ZEILE, nicht die der orders-Zeile
 *   `updatedAt`/`updatedBy` == wer/wann die ZUSATZFELDER zuletzt geschrieben hat
 *
 * Sie stumm ueber die Kopffelder zu schreiben waere genau der stille Fehler, den diese Runde
 * beseitigt: `rowVersion` haette dann je nach Endpunkt zwei Bedeutungen - `GET /api/orders` liefert
 * NUR die orders-Zeile, ohne jedes `extras` (`backend/src/domaene/orders.ts:152-155`). Der Kopf
 * behaelt deshalb seine Bedeutung, die Extras-Fassung bekommt einen eigenen Namen
 * (`extrasRowVersion`, `extrasUpdatedAt`, `extrasUpdatedBy`).
 *
 * BEFUND, GEMELDET UND NICHT REPARIERT (Fix-Runde 1): die fuenf Feldgruppen-PATCH und
 * `PATCH /:id/board-status` pruefen `order_extras.row_version`
 * (`backend/src/domaene/extras.ts:68-76` und `:129-135`), `DELETE /api/orders/:id` dagegen
 * `orders.row_version` (`backend/src/http/routes/orders.ts:429-435`). Die SPA reicht heute an ALLE
 * drei Wege `bestellung.rowVersion` durch (`App.tsx#drawerAendern`, `#boardGruppeAendern`,
 * `#drawerLoeschen`) - fuer die beiden Schreibwege ist das die falsche Zahl. Die richtige steht seit
 * dieser Runde unter `extrasRowVersion` bereit; das Umhaengen der Aufrufstellen samt der Fixturen
 * der Verdrahtungstests gehoert in einen eigenen Task und ist hier bewusst NICHT mitgemacht.
 *
 * Ueber Destrukturierung und Spread und NICHT ueber `ergebnis[feld] = wert` in einer Schleife: ein
 * indizierter Schreibzugriff mit einem Schluessel aus Fremddaten setzt bei `"__proto__"` den
 * PROTOTYPEN statt eine Eigenschaft (`JSON.parse` legt `__proto__` sehr wohl als eigene
 * Eigenschaft an). Der Spread benutzt CreateDataProperty und kennt diesen Umweg nicht - dasselbe
 * Fehlermuster, das im Projekt schon mehrfach eine Pruefung ausgehebelt hat.
 *
 * Ein Datensatz OHNE `extras` (die Liste) und einer mit `extras: null` (Bestellung ohne
 * Zusatzfeldzeile) laufen unveraendert durch - in beiden Faellen bleibt kein `extras`-Schluessel
 * stehen, damit es im Store nur EINE Form gibt.
 *
 * `supplierId` UND `supplier` werden auf BEIDEN Zweigen hergeleitet (siehe `supplierIdVonKopf` und
 * `supplierNameVonKopf` oben), bevor ueber `extras` entschieden wird - `bestellungenLaden` (Liste,
 * ohne `extras`) und `bestellungLaden` (Einzelabruf, mit `extras`) laufen durch dieselbe Herleitung
 * und geben damit dieselbe Form in den Store, genau wie es der Kopfkommentar von `bestellungenLaden`
 * fuer den Rest der Naht verlangt.
 *
 * Das ist bei `supplier` mehr als Ordnungsliebe: die Sammelroute liefert das Feld GAR NICHT, der
 * Einzelabruf ein OBJEKT. Ohne gemeinsame Herleitung haenge das Verhalten davon ab, ueber welchen
 * der beiden Wege ein Datensatz zuletzt hereinkam - und der eine der beiden stuerzt ab.
 *
 * `tasks` LAEUFT SEIT DEM 2026-08-15 DURCH `alsAufgabe()` (`api/aufgaben.ts`). Beide Lesewege
 * liefern die Aufgaben flach am Bestellobjekt mit (`backend/src/domaene/orders.ts`), und dort heisst
 * der Aufgabentitel `title`, waehrend die Oberflaeche ihn an vier Stellen als `name` liest. Bis
 * hierher zeigte deshalb JEDE geladene Aufgabe "Neue Aufgabe" (`PhasenListe.tsx`, Rueckfall bei
 * leerem Namen) - der Titel war da, nur unter einem Namen, den niemand las. Die Umbenennung sitzt
 * HIER, an der einen Naht, und nicht in den vier Lesestellen; die Begruendung samt Gegenrichtung
 * (`AUFGABEN_SPALTEN` beim Schreiben) steht im Kopf von `api/aufgaben.ts`.
 *
 * Auf BEIDEN Zweigen, vor der `extras`-Entscheidung - aus demselben Grund wie bei `supplier`: die
 * Form darf nicht davon abhaengen, ueber welchen Weg ein Datensatz zuletzt hereinkam.
 */
export function entschachtele<T>(datensatz: T): T {
  if (typeof datensatz !== "object" || datensatz === null || Array.isArray(datensatz)) {
    return datensatz;
  }
  const { extras, ...kopf } = datensatz as Record<string, unknown> & { extras?: unknown };
  const supplierId = supplierIdVonKopf(kopf);
  const supplierName = supplierNameVonKopf(kopf);
  // `supplier` wird ENTFERNT, wenn kein Name herauskommt - stehen bliebe sonst das Objekt, und genau
  // das ist der Absturz (siehe `supplierNameVonKopf`). `undefined` als Wert genuegt nicht: ein
  // vorhandener Schluessel mit `undefined` ueberlebt zwar jeden Lesezugriff hier, wuerde aber beim
  // naechsten Spread wieder mitreisen und dabei einen echten Namen UEBERSCHREIBEN - und er machte die
  // Form zwischen den beiden Lesewegen unterscheidbar. Der Schluessel faellt deshalb ganz weg, wie
  // bei einer Bestellung ohne Lieferanten.
  //
  // WAS DABEI STILL WEGFAELLT, damit es niemand suchen muss: mit dem Objekt verschwinden auch
  // `supplier.kLieferant` und `supplier.extras` (dort liegen `eur`, `shortName`, `logo`,
  // `paymentPlan`). Heute liest das an der BESTELLUNG niemand - die Oberflaeche holt den Lieferanten
  // ueber `supplierId` aus dem Lieferantenspeicher (`lieferantLaden`), und dort kommen dieselben
  // Felder ueber `api/lieferanten.ts#entschachtele` an. Wer sie kuenftig an der Bestellung braucht,
  // haengt sie hier flach daneben - nicht, indem er das Objekt stehen laesst.
  const { supplier: _supplierRoh, ...kopfOhneSupplier } = kopf;
  const kopfMitNaht: Record<string, unknown> = {
    ...kopfOhneSupplier,
    ...(supplierId === undefined ? {} : { supplierId }),
    ...(supplierName === undefined ? {} : { supplier: supplierName }),
    ...(Array.isArray(kopf.tasks) ? { tasks: kopf.tasks.map(alsAufgabe) } : {}),
    // `payments`: Server-Raten tragen `dueDate`/`paidAt`, die Oberflaeche liest `due`/`paid` (alsRate).
    ...(Array.isArray(kopf.payments) ? { payments: kopf.payments.map(alsRate) } : {}),
  };
  if (typeof extras !== "object" || extras === null || Array.isArray(extras)) {
    return kopfMitNaht as T;
  }
  const {
    orderId: _orderId,
    rowVersion: extrasRowVersion,
    updatedAt: extrasUpdatedAt,
    updatedBy: extrasUpdatedBy,
    // `board_status` kommt durch `oeffentlich()` rein mechanisch als `boardStatus` herein (keine
    // Sonderbehandlung dort, siehe Kopfkommentar an camelSchluessel) - JEDE Ansicht (Bestellzeile.tsx,
    // BestellungTab.tsx, lib/status.ts, orderList.ts, ...) liest aber `status`, nie `boardStatus`.
    // Ohne diese Umbenennung bleibt `status` fuer JEDE Bestellung `undefined`, `normalisierteStatus()`
    // (state/statusNormalisierung.ts) haelt das fuer "unbekannter Status" und ueberschreibt ihn hart
    // auf die erste Spalte ("Vorbereiten") - nicht nur fuer Bestellungen ohne Hub-Zeile, sondern fuer
    // JEDE, auch fuer eine, deren `board_status` gerade erst erfolgreich geschrieben wurde. Gemessen
    // am 2026-08-28 gegen Produktion: ein PATCH auf `board-status` schreibt den neuen Wert nachweislich
    // in `order_extras` (row_version steigt, Wert bleibt stehen), die Oberflaeche zeigt danach aber
    // weiterhin "Vorbereiten" - bis zu dieser Zeile.
    boardStatus,
    ...extrasFelder
  } = extras as Record<string, unknown>;
  // MESSUNG 2026-08-13 (Weg B zu Befund 3b): eine LEERE Extras-Spalte legt keinen Schluessel mehr
  // an. `Object.fromEntries` und nicht `ergebnis[feld] = wert`: der Schluessel stammt aus
  // Fremddaten, und ein indizierter Schreibzugriff setzt bei "__proto__" den PROTOTYPEN -
  // `fromEntries` benutzt CreateDataProperty und kennt diesen Umweg nicht, genau wie der Spread.
  const extrasOhneLeere = Object.fromEntries(
    Object.entries(extrasFelder).filter(([, wert]) => wert !== null && wert !== undefined),
  );
  return {
    ...kopfMitNaht,
    ...extrasOhneLeere,
    ...(boardStatus === null || boardStatus === undefined ? {} : { status: boardStatus }),
    extrasRowVersion,
    extrasUpdatedAt,
    extrasUpdatedBy,
  } as T;
}

